// usePushNotifications — the one way a page turns browser push on or off.
//
// The bell had a working "Enable desktop notifications" (permission prompt +
// service worker + subscription saved on the server). The notification settings
// page had a Push switch that only saved a preference flag, so a person could
// turn Push "On" and never be asked by the browser — and never get a push. Both
// surfaces now call this hook, so turning push on anywhere means the same thing.
//
//   const push = usePushNotifications();
//   push.status   'granted' | 'default' | 'denied' | 'unsupported' | 'disabled' | 'error'
//                 (the browser's permission, plus our derived states)
//   push.subscribed  true | false | null (not known yet) — whether THIS browser
//                 holds a push subscription. Permission stays 'granted' after
//                 disable(), so "on" is granted AND subscribed.
//   push.on       status === 'granted' && subscribed !== false
//   await push.enable()   → { ok, status, reason, message }
//   await push.disable()
//
// We never prompt on load; the browser dialog only appears from enable(), which
// must be called from a click.
import { useCallback, useEffect, useState } from 'react';
import { isPushSupported, currentPermission, enablePush, disablePush } from '../utils/pushSubscription';

const SW_URL = '/sw.js';

async function hasSubscription() {
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!registration) return false;
    return Boolean(await registration.pushManager.getSubscription());
  } catch {
    return null;
  }
}

// What to tell a person when push could not be turned on, in plain words.
export function pushBlockedMessage(result) {
  const status = result?.status;
  if (status === 'denied') {
    return 'Your browser is blocking notifications from this site. To allow them, select the padlock or site settings icon beside the web address, set Notifications to Allow, then turn Push on again.';
  }
  if (status === 'default') {
    return 'Notifications were not allowed. Turn Push on again and choose Allow when your browser asks.';
  }
  if (status === 'unsupported') {
    return 'This browser cannot show push notifications. Try an up-to-date Chrome, Edge, Firefox or Safari.';
  }
  if (status === 'disabled') {
    return 'Push notifications are not available right now. Please try again later.';
  }
  return 'Could not turn on push notifications. Please try again.';
}

export default function usePushNotifications() {
  const supported = isPushSupported();
  const [status, setStatus] = useState(() => (supported ? currentPermission() : 'unsupported'));
  const [subscribed, setSubscribed] = useState(null);
  const [busy, setBusy] = useState(false);

  // Re-read the real browser permission: on mount, and when the person comes
  // back to the tab (they may have changed it in the browser's site settings).
  const refresh = useCallback(async () => {
    if (!isPushSupported()) return;
    const permission = currentPermission();
    setStatus(permission);
    setSubscribed(permission === 'granted' ? await hasSubscription() : false);
  }, []);

  useEffect(() => {
    refresh();
    if (typeof document === 'undefined') return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const result = await enablePush();
      setStatus(result.ok ? 'granted' : (result.status || 'error'));
      setSubscribed(result.ok ? true : await hasSubscription());
      return { ...result, message: result.ok ? '' : pushBlockedMessage(result) };
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const result = await disablePush();
      if (isPushSupported()) setStatus(currentPermission());
      setSubscribed(result.ok ? false : await hasSubscription());
      return result;
    } finally {
      setBusy(false);
    }
  }, []);

  const on = status === 'granted' && subscribed !== false;
  return { supported, status, subscribed, on, busy, enable, disable, refresh };
}
